/**
 * Reia Mega Nav Controller
 * Full-screen luxury navigation system
 */

class ReiaMegaNavMenu {
  constructor(element) {
    if (!element) {
      console.warn('ReiaMegaNavMenu: No element provided');
      return;
    }

    this.menu = element;
    this.trigger = document.querySelector('[data-reia-mega-nav-menu-trigger]');
    this.closeBtns = element.querySelectorAll('[data-reia-mega-nav-menu-close]');
    this.categoryBtns = element.querySelectorAll('[data-category]');
    this.contentPane = element.querySelector('[data-reia-mega-nav-content-pane]');
    this.leftPane = element.querySelector('.reia-mega-nav-category-nav');
    this.mainList = element.querySelector('[data-nav-level="main"]');
    this.subMenuLists = element.querySelectorAll('[data-nav-level="submenu"]');
    this.backBtns = element.querySelectorAll('[data-nav-back]');

    // State
    this.activeCategory = 'featured';
    this.scrollPosition = 0;

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);

    // Single AbortController for all event listeners — lets destroy() remove them all at once
    this._abortController = new AbortController();

    // Cache breakpoint query — re-used in close() and handleKeydown()
    this._isDesktop = window.matchMedia('(min-width: 1025px)');

    this.init();
  }

  init() {
    const signal = this._abortController.signal;

    // Trigger button
    if (this.trigger) {
      this.trigger.addEventListener('click', this.open, { signal });
    }

    // Close buttons (desktop + mobile — both share [data-reia-mega-nav-menu-close])
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }, { signal });
    });

    // Desktop category buttons
    this.categoryBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.selectCategory(e), { signal });
    });

    // Back buttons
    this.backBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goBack(), { signal });
    });

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeydown, { signal });

    // Backdrop click
    this.menu.addEventListener('click', this.handleBackdropClick, { signal });

    // When clicking any navigation link, close the dialog immediately so
    // the modal top-layer and body scroll-lock don't block the page while
    // the browser follows the href.
    this.menu.addEventListener('click', (e) => {
      const link = e.target.closest('[data-reia-mega-nav-content-pane] a[href], [data-reia-mega-nav-category-nav] a[href]');
      if (link && link.href && link.getAttribute('href') !== '#') {
        if (typeof this.menu.close === 'function') {
          this.menu.close();
        }
        this.unlockBodyScroll();
        document.body.classList.remove('reia-mega-nav-menu-open');
      }
    }, { signal });

    // Scroll fade hints — detect when user has scrolled to the bottom
    this.initScrollFadeHints(signal);
  }

  /**
   * Scroll fade hints — toggle 'scrolled-end' class on scrollable containers
   */
  initScrollFadeHints(signal) {
    const checkScrollEnd = (el) => {
      const threshold = 10;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      const canScroll = el.scrollHeight > el.clientHeight + threshold;
      el.classList.toggle('scrolled-end', atBottom || !canScroll);
    };

    // Left pane
    if (this.leftPane) {
      this.leftPane.addEventListener('scroll', () => checkScrollEnd(this.leftPane), { signal, passive: true });
      // Initial check after menu opens
      this._checkLeftPaneScroll = () => checkScrollEnd(this.leftPane);
    }

    // Submenus — observe class changes to detect when they become active
    this.subMenuLists.forEach(sub => {
      sub.addEventListener('scroll', () => checkScrollEnd(sub), { signal, passive: true });
    });

    // Store for use in open()
    this._checkScrollEnd = checkScrollEnd;
  }

  /**
   * Open menu
   */
  open() {
    // Clear any leftover state from a previous close or exit animation
    this.menu.classList.remove('is-closing');
    this.menu.classList.remove('has-overlay');
    this.menu.classList.remove('reia-mega-nav-menu--exiting');

    // Show dialog
    if (typeof this.menu.showModal === 'function') {
      this.menu.showModal();
    } else {
      this.menu.setAttribute('open', '');
    }

    // Lock body scroll
    this.lockBodyScroll();

    // Trigger slide-in transition — add .is-open on next frame so the
    // browser registers the starting state (translateX(-100%)) before
    // transitioning to the end state (translateX(0)).
    requestAnimationFrame(() => {
      this.menu.classList.add('is-open');
      this.menu.classList.add('has-overlay');
    });

    // Update trigger state
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    // Check scroll fade hints after menu slide-in completes
    setTimeout(() => {
      if (this._checkLeftPaneScroll) this._checkLeftPaneScroll();
    }, 400);

    // Add body class
    document.body.classList.add('reia-mega-nav-menu-open');

    // Focus first category. showModal() moves focus into the dialog (native
    // focus trap); this overrides that default to land on the first menu item.
    // preventScroll avoids any layout shift caused by focus-into-view.
    requestAnimationFrame(() => {
      const firstCategory = this.menu.querySelector('.category-item');
      firstCategory?.focus({ preventScroll: true });
    });
  }

  /**
   * Close menu
   */
  close() {
    // Guard: ignore if already closing
    if (this._isClosing) return;
    this._isClosing = true;

    const isDesktop = this._isDesktop.matches;
    const hasOverlay = this.menu.classList.contains('has-overlay');

    // If overlay is showing, fade it out first (400ms matches CSS), then slide out
    const startSlideOut = () => {
      if (isDesktop) {
        this.menu.classList.remove('is-open');
        this.menu.classList.add('is-closing');

        // Wait for the left pane's slide-out transition (it trails the right pane
        // so it finishes last). Fallback timeout guards against transitionend not firing.
        this._waitForSlideOut(() => {
          this.menu.classList.remove('is-closing');
          this._finishClose();
          this._isClosing = false;
        });
      } else {
        // Mobile: close immediately
        this.menu.classList.remove('is-open');
        this._finishClose();
        this._isClosing = false;
      }
    };

    if (hasOverlay) {
      this.menu.classList.remove('has-overlay');
      setTimeout(startSlideOut, 40);
    } else {
      startSlideOut();
    }
  }

  /**
   * Wait for the left pane's transform transition to finish, then run callback.
   * Uses transitionend for accurate timing, with a fallback timeout in case the
   * event doesn't fire (e.g. transition cancelled or element detached).
   */
  _waitForSlideOut(done) {
    if (!this.leftPane) {
      // No left pane — nothing to wait for, fall back to expected duration
      setTimeout(done, 500);
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(fallback);
      this.leftPane.removeEventListener('transitionend', onEnd);
      done();
    };

    const onEnd = (e) => {
      // Ignore bubbled events from descendants and non-transform properties
      if (e.target !== this.leftPane || e.propertyName !== 'transform') return;
      finish();
    };

    this.leftPane.addEventListener('transitionend', onEnd);
    // Fallback: expected duration is 0.14s delay + 0.3s duration = 440ms, add buffer
    const fallback = setTimeout(finish, 550);
  }

  /**
   * Finalise close (after exit animation on desktop)
   */
  _finishClose() {
    // Clean up exit animation class
    this.menu.classList.remove('reia-mega-nav-menu--exiting');

    // Return to main menu (desktop)
    this.subMenuLists.forEach(submenu => {
      submenu.classList.remove('active');
      submenu.hidden = true;
    });
    if (this.mainList) {
      this.mainList.classList.remove('hidden');
    }

    // Close dialog
    if (typeof this.menu.close === 'function') {
      this.menu.close();
    } else {
      this.menu.removeAttribute('open');
    }

    // Unlock body scroll
    this.unlockBodyScroll();

    // Update trigger state and return focus synchronously (dialog is already
    // closed at this point, so focus can safely return to the trigger).
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.focus({ preventScroll: true });
    }

    // Remove body class
    document.body.classList.remove('reia-mega-nav-menu-open');

    // Reset to featured category — dialog is already hidden so no visual flicker
    this.resetToFeatured();
  }

  /**
   * Select category (desktop)
   */
  selectCategory(event) {
    const button = event.currentTarget;
    const category = button.dataset.category;
    const hasSubmenu = button.dataset.hasSubmenu === 'true';

    if (!category) return;

    // If category has sub-menu, open it and show its content
    if (hasSubmenu && this.mainList) {
      this.openSubmenu(category);
      // Also load the parent category's content
      this.loadCategoryContent(category);
      return;
    }

    // Otherwise, just switch content in right pane
    if (!this.contentPane) return;

    // Update active state
    this.categoryBtns.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-current', 'false');
    });

    button.classList.add('active');
    button.setAttribute('aria-current', 'page');

    // Switch content
    this.loadCategoryContent(category);

    // Update state
    this.activeCategory = category;

    // Scroll content pane to top
    if (this.contentPane) {
      this.contentPane.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Load category content
   */
  loadCategoryContent(category) {
    if (!this.contentPane) return;

    // Only these categories show the right content pane
    const PANE_CATEGORIES = new Set([
      'collections', 'find-us',
      'engagement-diamond-colour', 'engagement-shapes',
      'engagement-gold-colour', 'engagement-for-her'
    ]);

    const allContent = this.contentPane.querySelectorAll('[data-category-content]');
    let foundMatch = false;
    let resolvedCategory = category;

    allContent.forEach(content => {
      const contentCategory = content.dataset.categoryContent;
      const isMatch = contentCategory === category;

      if (isMatch) {
        content.hidden = false;
        content.setAttribute('aria-hidden', 'false');
        foundMatch = true;
      } else {
        content.hidden = true;
        content.setAttribute('aria-hidden', 'true');
      }
    });

    // Fallback: if no matching content, walk up the parent chain
    // until we find a category that has a content panel
    if (!foundMatch) {
      let fallbackCategory = category;
      while (!foundMatch && fallbackCategory) {
        // Find the button for this category, then its containing submenu's data-parent
        const btn = this.menu.querySelector(`[data-category="${fallbackCategory}"]`);
        const containingSubmenu = btn ? btn.closest('.category-list--submenu[data-parent]') : null;
        fallbackCategory = containingSubmenu ? containingSubmenu.dataset.parent : null;

        if (fallbackCategory) {
          allContent.forEach(content => {
            if (content.dataset.categoryContent === fallbackCategory) {
              content.hidden = false;
              content.setAttribute('aria-hidden', 'false');
              foundMatch = true;
              resolvedCategory = fallbackCategory;
            }
          });
        }
      }
    }

    // Toggle right pane visibility — only show for collections & stores
    this.menu.classList.toggle('reia-mega-nav-menu--has-pane', PANE_CATEGORIES.has(resolvedCategory));

    this.toggleEmptyState(!foundMatch, category);
  }

  /**
   * Toggle empty state message
   */
  toggleEmptyState(show, category) {
    let emptyState = this.contentPane.querySelector('[data-empty-state]');

    if (show) {
      // Create empty state if it doesn't exist
      if (!emptyState) {
        emptyState = document.createElement('div');
        emptyState.setAttribute('data-empty-state', '');
        emptyState.style.cssText = 'grid-column: 1 / -1; text-align: center; padding: 80px 40px;';
        emptyState.innerHTML = `
          <h3 style="font-size: 24px; color: var(--reia-mega-nav-text-dark); margin-bottom: 16px; font-weight: 500;">
            No Content Configured
          </h3>
          <p style="font-size: 16px; color: var(--reia-mega-nav-text-light); margin-bottom: 8px; line-height: 1.6;">
            This category needs content to display.
          </p>
          <p style="font-size: 14px; color: var(--reia-mega-nav-text-light);">
            Category: <strong>${category}</strong>
          </p>
        `;
        this.contentPane.appendChild(emptyState);
      }

      emptyState.hidden = false;
      emptyState.setAttribute('aria-hidden', 'false');
    } else {
      // Hide empty state if it exists
      if (emptyState) {
        emptyState.hidden = true;
        emptyState.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Open submenu in left pane
   */
  openSubmenu(parentCategory) {
    // Hide main list
    if (this.mainList) {
      this.mainList.classList.add('hidden');
    }

    // Show corresponding submenu
    this.subMenuLists.forEach(submenu => {
      if (submenu.dataset.parent === parentCategory) {
        submenu.classList.add('active');
        submenu.hidden = false;
        // Check scroll fade after transition settles
        if (this._checkScrollEnd) {
          requestAnimationFrame(() => this._checkScrollEnd(submenu));
        }
      } else {
        submenu.classList.remove('active');
        submenu.hidden = true;
      }
    });
  }

  /**
   * Go back one level
   */
  goBack() {
    // Find the currently active submenu
    const activeSubmenu = this.menu.querySelector('[data-nav-level="submenu"].active');
    if (!activeSubmenu) return;

    const currentParent = activeSubmenu.dataset.parent;

    // Hide the current submenu
    activeSubmenu.classList.remove('active');
    activeSubmenu.hidden = true;

    // Find which submenu contains a button with data-category matching currentParent
    // That submenu is the one we need to go back to
    let parentSubmenu = null;
    this.subMenuLists.forEach(submenu => {
      const btn = submenu.querySelector(`[data-category="${currentParent}"]`);
      if (btn) {
        parentSubmenu = submenu;
      }
    });

    if (parentSubmenu) {
      // Go back to parent submenu
      parentSubmenu.classList.add('active');
      parentSubmenu.hidden = false;
      // Load the PARENT submenu's category (not the child we're leaving)
      // so the right pane resets to the parent's panel, not the child's
      const parentCategory = parentSubmenu.dataset.parent;
      if (parentCategory) {
        this.loadCategoryContent(parentCategory);
      } else {
        this.resetToFeatured();
      }
      // Re-check scroll fade
      if (this._checkScrollEnd) {
        requestAnimationFrame(() => this._checkScrollEnd(parentSubmenu));
      }
    } else {
      // No parent submenu found — go back to main list
      if (this.mainList) {
        this.mainList.classList.remove('hidden');
      }
      this.resetToFeatured();
    }
  }

  /**
   * Reset to featured category
   */
  resetToFeatured() {
    // Reset desktop
    this.categoryBtns.forEach(btn => {
      if (btn.dataset.category === 'featured') {
        btn.classList.add('active');
        btn.setAttribute('aria-current', 'page');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-current', 'false');
      }
    });

    // Reset content
    this.loadCategoryContent('featured');
    this.activeCategory = 'featured';

    // Scroll to top
    if (this.contentPane) {
      this.contentPane.scrollTop = 0;
    }
  }

  /**
   * Lock body scroll. Compensates for the disappearing scrollbar by padding
   * the body, preventing the layout jump that occurs when overflow changes
   * to hidden and the vertical scrollbar is removed.
   */
  lockBodyScroll() {
    this.scrollPosition = window.scrollY;

    // Measure the scrollbar width BEFORE applying overflow:hidden
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  /**
   * Unlock body scroll
   */
  unlockBodyScroll() {
    document.body.style.paddingRight = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this.scrollPosition);
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    if (!this.isOpen()) return;

    // ESC key
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }

    // Arrow keys for category navigation (desktop only)
    if (this._isDesktop.matches) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const focusedElement = document.activeElement;
        const isCategoryButton = focusedElement && focusedElement.classList.contains('category-item');

        if (isCategoryButton) {
          event.preventDefault();
          const buttons = Array.from(this.categoryBtns);
          const currentIndex = buttons.indexOf(focusedElement);

          let nextIndex;
          if (event.key === 'ArrowDown') {
            nextIndex = currentIndex + 1 < buttons.length ? currentIndex + 1 : 0;
          } else {
            nextIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : buttons.length - 1;
          }

          buttons[nextIndex]?.focus();
        }
      }
    }
  }

  /**
   * Handle backdrop click
   */
  handleBackdropClick(event) {
    // Only close if clicking directly on the dialog backdrop
    if (event.target === this.menu) {
      this.close();
    }
  }

  /**
   * Check if menu is open
   */
  isOpen() {
    return this.menu.hasAttribute('open') || this.menu.open;
  }

  /**
   * Destroy instance — removes every listener registered with this._abortController
   */
  destroy() {
    this._abortController.abort();
    this.unlockBodyScroll();
    document.body.classList.remove('reia-mega-nav-menu-open');
  }
}

/**
 * Reia Mega Nav Search Drawer
 * Handles the slide-in search drawer triggered by the reia mega nav header's [data-search] button.
 */
class ReiaMegaNavSearchDrawer {
  constructor(drawer) {
    if (!drawer) return;

    this.drawer = drawer;
    this.searchBtns = document.querySelectorAll('[data-search]');
    this.closeBtn = drawer.querySelector('[data-close-icon]');
    this.overlay = drawer.querySelector('[data-overlay]');
    this.input = drawer.querySelector('[data-input]');
    this.form = drawer.querySelector('[data-quick-search-form]');
    this.resultsContainer = drawer.querySelector('[data-results]');
    this.clearBtn = drawer.querySelector('[data-clear]');

    this._abortController = new AbortController();
    this._isClosing = false;
    this._searchCache = {};

    this.init();
  }

  init() {
    const signal = this._abortController.signal;

    this.searchBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.toggle(e), { signal, capture: true });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }, { signal, capture: true });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close(), { signal });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });

    // Predictive search: listen for input
    if (this.input) {
      this.input.addEventListener('input', (e) => this._handleInput(e), { signal });
    }

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._resetSearch();
      }, { signal });
    }
  }

  /** Handle search input — fetch predictive results */
  _handleInput(e) {
    const value = e.target.value;

    if (value === '') {
      this._resetSearch();
      return;
    }

    // Toggle active classes for CSS visibility
    this.clearBtn?.classList.toggle('quick-search--visible', true);
    this.input?.parentNode?.classList.toggle('active', true);
    this.form?.classList.toggle('active', true);

    this._fetchResults(value);
  }

  /** Fetch predictive search results from Shopify API */
  _fetchResults(searchTerm) {
    const queryKey = searchTerm.replaceAll(' ', '-').toLowerCase();

    // Serve from cache if available
    if (this._searchCache[queryKey]) {
      this._renderResults(this._searchCache[queryKey]);
      return;
    }

    const params = new URLSearchParams();
    params.set('section_id', 'predictive-search');
    params.set('q', searchTerm);
    if (window.theme?.searchableFields) {
      params.set('resources[options][fields]', window.theme.searchableFields);
    }

    const url = (window.theme?.routes?.predictive_search_url || '/search/suggest') + '?' + params;

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(text => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const el = doc.querySelector('#shopify-section-predictive-search');
        if (!el) return;

        const html = el.innerHTML;
        this._searchCache[queryKey] = html;
        this._renderResults(html);
      })
      .catch(err => {
        console.error('[ReiaMegaNavSearch] Predictive search error:', err);
      });
  }

  /** Write results HTML into the results container */
  _renderResults(html) {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = html;
    }
  }

  /** Clear input & results, remove active classes */
  _resetSearch() {
    if (this.input) this.input.value = '';
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    this.clearBtn?.classList.remove('quick-search--visible');
    this.input?.parentNode?.classList.remove('active');
    this.form?.classList.remove('active');
    this.input?.focus();
  }

  isOpen() {
    return this.drawer.getAttribute('aria-hidden') === 'false';
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.drawer.classList.remove('is-closing');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('quick-search-open', 'true');

    // Double rAF so display:block registers before transform transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawer.classList.add('quick-search--visible');
      });
    });

    // Focus input once the drawer has finished sliding in
    this._waitForSlideIn(() => {
      this.input?.focus({ preventScroll: true });
    });
  }

  close() {
    if (this._isClosing) return;

    const isDesktop = window.matchMedia('(min-width: 960px)').matches;
    this.drawer.classList.remove('quick-search--visible');
    document.body.setAttribute('quick-search-open', 'false');

    if (!isDesktop) {
      this.drawer.setAttribute('aria-hidden', 'true');
      return;
    }

    // Desktop: play slide-out transition before hiding
    this._isClosing = true;
    this.drawer.classList.add('is-closing');

    this._waitForSlideOut(() => {
      this.drawer.classList.remove('is-closing');
      this.drawer.setAttribute('aria-hidden', 'true');
      this._isClosing = false;
    });
  }

  _waitForSlideIn(done) { this._waitForTransform(done, 600); }
  _waitForSlideOut(done) { this._waitForTransform(done, 600); }

  _waitForTransform(done, fallbackMs) {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(fallback);
      this.drawer.removeEventListener('transitionend', onEnd);
      done();
    };
    const onEnd = (e) => {
      if (e.target !== this.drawer || e.propertyName !== 'transform') return;
      finish();
    };
    this.drawer.addEventListener('transitionend', onEnd);
    const fallback = setTimeout(finish, fallbackMs);
  }

  destroy() {
    this._abortController.abort();
  }
}

/**
 * Reia Mega Nav Header Sticky
 *
 * The base theme's header JS (theme.js) only registers on sections with
 * data-section-type="header". Our reia mega nav header uses "header-reia-mega-nav", so none
 * of the theme's IntersectionObserver / is-sticky / height-tracking logic runs.
 * This class replicates just those three pieces, scoped to the reia mega nav header.
 */
class ReiaMegaNavHeaderSticky {
  constructor(header) {
    if (!header) return;

    this.header = header;
    this.sentinel = document.querySelector('[data-header-space]');

    this._abortController = new AbortController();
    this._intersectionObserver = null;
    this._resizeObserver = null;

    this.init();
  }

  init() {
    // Toggle is-sticky when the sentinel leaves the viewport
    if (this.sentinel && 'IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver(([entry]) => {
        const stuck = !entry.isIntersecting;
        this.header.classList.toggle('is-sticky', stuck);
        document.documentElement.classList.toggle('sticky-header-active', stuck);
      });
      this._intersectionObserver.observe(this.sentinel);
    } else {
      // No sentinel available — treat as always sticky (safe fallback)
      this.header.classList.add('is-sticky');
      document.documentElement.classList.add('sticky-header-active');
    }

    // Track header height in a CSS variable so the hero can overlap correctly
    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(([entry]) => {
        const rect = entry.target.getBoundingClientRect();
        const height = rect ? rect.height : entry.target.offsetHeight;
        document.documentElement.style.setProperty('--height-header', `${Math.ceil(height)}px`);
      });
      this._resizeObserver.observe(this.header);
    } else {
      // Fallback: measure once
      document.documentElement.style.setProperty(
        '--height-header',
        `${Math.ceil(this.header.offsetHeight)}px`
      );
    }
  }

  destroy() {
    this._intersectionObserver?.disconnect();
    this._resizeObserver?.disconnect();
    this._abortController.abort();
  }
}

/**
 * Initialize on DOM ready
 */
function initReiaMegaNavMenu() {
  const menuElement = document.querySelector('[data-reia-mega-nav-menu]');

  if (menuElement && !menuElement._reiaMegaNavMenu) {
    const menu = new ReiaMegaNavMenu(menuElement);
    menuElement._reiaMegaNavMenu = menu;

    // Expose to window for debugging
    if (typeof window !== 'undefined') {
      window.reiaMegaNavMenu = menu;
    }
  }

  // Allow external buttons to open the menu to a specific category
  document.querySelectorAll('[data-open-mega-nav-category]').forEach(btn => {
    btn.addEventListener('click', () => {
      const category = btn.dataset.openMegaNavCategory;
      const menuInstance =
        window.reiaMegaNavMenu ||
        document.querySelector('[data-reia-mega-nav-menu]')?._reiaMegaNavMenu;
      if (!menuInstance || !category) return;
      menuInstance.open();
      // Wait for menu to be visible before selecting category
      requestAnimationFrame(() => {
        const catBtn = menuInstance.menu.querySelector(`[data-category="${category}"]`);
        if (catBtn) {
          menuInstance.selectCategory({ currentTarget: catBtn });
        }
      });
    });
  });

  const drawerElement = document.querySelector('[data-quick-search]');
  if (drawerElement && !drawerElement._reiaMegaNavSearchDrawer) {
    const drawer = new ReiaMegaNavSearchDrawer(drawerElement);
    drawerElement._reiaMegaNavSearchDrawer = drawer;

    if (typeof window !== 'undefined') {
      window.reiaMegaNavSearchDrawer = drawer;
    }
  }

  const headerElement = document.querySelector('[data-section-type="header-reia-mega-nav"]');
  if (headerElement && !headerElement._reiaMegaNavHeaderSticky) {
    const sticky = new ReiaMegaNavHeaderSticky(headerElement);
    headerElement._reiaMegaNavHeaderSticky = sticky;

    if (typeof window !== 'undefined') {
      window.reiaMegaNavHeaderSticky = sticky;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReiaMegaNavMenu);
} else {
  initReiaMegaNavMenu();
}

// Reset menu state when page is restored from bfcache
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    const trigger = document.querySelector('[data-reia-mega-nav-menu-trigger]');
    const menu = document.querySelector('[data-reia-mega-nav-menu]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (menu) {
      menu.classList.remove('is-open', 'is-closing', 'has-overlay', 'reia-mega-nav-menu--exiting');
      // Use dialog.close() to properly exit modal state and remove from top layer
      if (menu.open && typeof menu.close === 'function') {
        menu.close();
      }
    }
    // Unlock body scroll in case it was locked when the page was cached
    document.body.style.paddingRight = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.classList.remove('reia-mega-nav-menu-open');
  }
});

// Shopify theme editor support
if (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initReiaMegaNavMenu);
  document.addEventListener('shopify:section:unload', () => {
    if (window.reiaMegaNavMenu) {
      window.reiaMegaNavMenu.destroy();
    }
    if (window.reiaMegaNavSearchDrawer) {
      window.reiaMegaNavSearchDrawer.destroy();
    }
    if (window.reiaMegaNavHeaderSticky) {
      window.reiaMegaNavHeaderSticky.destroy();
    }
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ReiaMegaNavMenu;
}
